import { z } from "zod";

import { TABAN_SHIP_COMPANY } from "./shipping-config";
import { stockPhotoUrl } from "./storage-config";

// Texts and images of the public site that staff edit in the panel («متن و تصویر صفحات»).
// A block is a flat set of string fields, each with its label and input for the panel's
// form, its limits, and the value the site shows until staff save the block. Pure module:
// the API validates and reads with it, the panel builds its forms from it, and the site
// only sees the result (site.content).

type FieldKind =
  | "text"
  | "textarea"
  | "lines"
  | "image"
  | "phone"
  | "choice"
  | "number"
  | "numbers"
  | "links";

export type SiteField = {
  label: string;
  /**
   * text: one line; textarea: a paragraph; lines: a list, one item per line; image: an
   * uploaded site image; phone: a phone number; choice: one of `options`; number: a whole
   * number inside `range`; numbers: whole numbers inside `range`, separated by commas;
   * links: a menu, one «عنوان | /مسیر» per line (see menuLinks)
   */
  kind: FieldKind;
  max: number;
  /** links: the most links the menu may hold */
  items?: number;
  /** what the site shows until staff save the block */
  initial: string;
  /** empty allowed: the site then leaves the element out */
  optional?: boolean;
  options?: readonly { value: string; label: string }[];
  /** number / numbers: smallest and largest value allowed */
  range?: readonly [number, number];
  /**
   * earlier defaults that turned out wrong: saving a block stores every field, so a stored
   * value equal to one of them was never staff's choice and is read as today's default
   */
  retired?: readonly string[];
};

export type SiteBlock = {
  title: string;
  /** where the block appears, for the panel */
  description: string;
  fields: Record<string, SiteField>;
  /** a rule across fields of the checked block; the Persian refusal, or null when it holds */
  check?: (values: Record<string, string>) => string | null;
};

const text = (label: string, initial: string, max = 60): SiteField => ({
  label,
  kind: "text",
  max,
  initial,
});
const paragraph = (label: string, initial: string, max = 300): SiteField => ({
  label,
  kind: "textarea",
  max,
  initial,
});
const image = (label: string, initial: string): SiteField => ({
  label,
  kind: "image",
  max: 2048,
  initial,
});
const list = (label: string, items: string[], max = 600): SiteField => ({
  label,
  kind: "lines",
  max,
  initial: items.join("\n"),
});
const whole = (label: string, initial: number, range: readonly [number, number]): SiteField => ({
  label,
  kind: "number",
  max: 3,
  initial: String(initial),
  range,
});
const optional = (field: SiteField): SiteField => ({ ...field, optional: true });
/** a field whose earlier defaults are read as the current one (see SiteField.retired) */
const replacing = (field: SiteField, ...retired: string[]): SiteField => ({ ...field, retired });
/**
 * a header menu whose earlier default jumped to homepage sections (/#tools, /#branches…): a row
 * still holding that default (either spelling) reads as the new one; a menu staff wrote stays
 */
const redirected = (field: SiteField, earlier: string[], ...later: string[]) => {
  const old = earlier.join("\n");
  return replacing(field, old, old.replaceAll("تأمین", "تامین"), ...later);
};
/** a menu: one «عنوان | /مسیر» per line, at most `most` links */
const menu = (label: string, items: string[], most = 12): SiteField => ({
  label: `${label} (هر خط: عنوان | /مسیر)`,
  kind: "links",
  max: 1200,
  initial: items.join("\n"),
  items: most,
});

const faPlain = new Intl.NumberFormat("fa-IR", { useGrouping: false });

/** The whole numbers of a `numbers` field, e.g. "12, 24, 36" */
export const numbers = (value: string) =>
  value
    .split(/[\s,،]+/)
    .filter(Boolean)
    .map(Number);

/** `{name}` placeholders of a staff text filled in, e.g. «{خودرو} را از نزدیک ببینید» */
export const fillText = (template: string, values: Record<string, string>) =>
  template.replace(/\{([^{}]+)\}/g, (token, name: string) => values[name.trim()] ?? token);

const slotsHelp =
  "در متن‌ها {خودرو} نام مدل، {شعبه} شعبه‌ی آن، {روز} روزهای کاری تحویل و {گارانتی} گارانتی مدل می‌شود.";
/** a vehicle page text filled per model (fillVehicleText); its label names the placeholders */
const slotted = (field: SiteField): SiteField => ({
  ...field,
  label: `${field.label}؛ جای‌ها: {خودرو} {شعبه} {روز} {گارانتی}`,
});

/** What the vehicle page's placeholders stand for */
export type VehicleSlots = {
  name: string;
  branchName: string;
  deliveryDays: number;
  warranty: string;
};

/**
 * A text of the vehicle page (بلوک «صفحه هر خودرو») filled in for one model: {خودرو},
 * {شعبه} (the block's `anyBranch` for a model without a branch), {روز} and {گارانتی}
 */
export const fillVehicleText = (template: string, vehicle: VehicleSlots, anyBranch: string) =>
  fillText(template, {
    خودرو: vehicle.name,
    شعبه: vehicle.branchName || anyBranch,
    روز: faPlain.format(vehicle.deliveryDays),
    گارانتی: vehicle.warranty,
  });

const monthsText = "مدت‌های بازپرداخت به ماه، با کاما جدا (ماشین‌حساب صفحه اصلی و صفحه هر خودرو)";

/** a static page (/about, /terms, …): title, optional lede and date, body, search description */
const page = (copy: {
  title: string;
  lede: string;
  updated: string;
  body: string[];
  metaDescription: string;
}) => ({
  title: text("عنوان صفحه", copy.title, 60),
  lede: optional(paragraph("متن زیر عنوان (خالی: نمایش داده نمی‌شود)", copy.lede, 240)),
  updated: optional(
    text(
      "تاریخ به‌روزرسانی، مثلاً «آخرین به‌روزرسانی: مهر ۱۴۰۵» (خالی: نمایش داده نمی‌شود)",
      copy.updated,
      60,
    ),
  ),
  body: paragraph(
    "متن صفحه: بین دو بند یک خط خالی بگذارید؛ خطی که با «## » شروع شود زیرعنوان می‌شود",
    copy.body.join("\n"),
    8000,
  ),
  metaDescription: paragraph("توضیح صفحه برای گوگل", copy.metaDescription, 160),
});

const legalReview = "پیش از انتشار، متن را با مشاور حقوقی بازبینی کنید.";

/** «حقوق شما» of the privacy page, now that the panel exports data and takes deletion requests */
const PRIVACY_RIGHTS =
  "اطلاعات حساب و خودروی خود را در پنل مشتریان می‌بینید و در «پروفایل» اصلاح می‌کنید؛ از همان‌جا شماره موبایل را تغییر می‌دهید، نسخه‌ای از اطلاعاتتان را دریافت می‌کنید یا درخواست حذف حساب می‌دهید. فاکتورها، پرداخت‌ها و قراردادها طبق قانون نگه داشته می‌شود.";
const PRIVACY_RIGHTS_BEFORE =
  "اطلاعات حساب و خودروی خود را در پنل مشتریان می‌بینید؛ برای اصلاح یا حذف اطلاعات با شعبه تماس بگیرید.";

/** a page whose earlier default body (made from today's by `earlier`) reads as today's default */
const withRetiredBody = <Fields extends { body: SiteField }>(
  fields: Fields,
  earlier: (body: string) => string,
): Fields => ({ ...fields, body: replacing(fields.body, earlier(fields.body.initial)) });

const ABOUT_STORY = [
  "## داستان ما",
  "تابان خودرو، با نام شرکتی اقتصاد تابان فردای کیش، کار خود را از منطقه آزاد کیش آغاز کرد؛ جایی که از نزدیک با تجارت بین‌المللی، برندهای معتبر جهانی و نیازهای بازار خودرو آشنا شدیم.",
  "",
  "با تکیه بر همین تجربه، مسیر خود را به سرزمین اصلی گسترش دادیم تا میان ظرفیت‌های مناطق آزاد و بازار ایران ارتباطی منسجم و حرفه‌ای بسازیم.",
];

/** the subsidiaries row of the body before they moved to their own «خدمات» field (brochure, مهر ۱۴۰۵) */
const ABOUT_SUBSIDIARIES_BEFORE = [
  "## زیرمجموعه‌های تابان",
  "تابان خودرو: واردات خودروهای صفر کیلومتر، فروش و مشاوره انتخاب خودرو.",
  "",
  "تابان سرویس: خدمات پس از فروش؛ سرویس و تعمیرات تخصصی، تأمین قطعات یدکی و دیتیلینگ خودرو.",
  "",
  "تابان کشتی: حمل دریایی خودرو از چین به امارات و عمان و از عمان به بنادر ایران، با هماهنگی و پیگیری مراحل حمل.",
  "",
  "تابان تاکسی و رنت: خدمات تاکسی برای شهروندان و مسافران و اجاره خودروهای لوکس و اقتصادی در جزیره کیش.",
];

/** «خدمات یکپارچه خودرو» of the brochure: one «## » per subsidiary, its tagline, text and «- » items */
const ABOUT_SERVICES = [
  "## تابان خودرو",
  "واردات و فروش خودروهای صفر کیلومتر",
  "نمایندگی رسمی تویوتا، لکسوس و نیسان؛ با تکیه بر تجربه و تخصص، مسیری مطمئن و شفاف برای دسترسی به خودروهای معتبر جهانی فراهم می‌کنیم.",
  "- واردات خودروهای صفر کیلومتر",
  "- فروش و مشاوره انتخاب خودرو",
  "",
  "## تابان سرویس",
  "خدمات پس از فروش",
  "همراهی ما پس از خرید ادامه دارد؛ با خدمات فنی، تعمیرات تخصصی و تأمین قطعات، برای حفظ کیفیت خودرو و تجربه‌ای مطمئن از مالکیت.",
  "- سرویس و تعمیرات تخصصی",
  "- تأمین قطعات یدکی",
  "- دیتیلینگ خودرو",
  "- رسیدگی به درخواست‌های خدمات",
  "",
  "## تابان تاکسی و رنت",
  "همراه شما در کیش",
  "خدمات تاکسی برای شهروندان و مسافران و اجاره خودروهای لوکس و اقتصادی در جزیره کیش؛ با تمرکز بر راحتی، کیفیت و پشتیبانی.",
  "- خدمات تاکسی برای مسافران و شهروندان",
  "- اجاره خودرو در جزیره کیش",
  "- پشتیبانی مشتریان",
  "",
  "## تابان کشتی",
  "حمل‌ونقل دریایی خودرو",
  "حمل دریایی خودرو از چین به امارات و عمان و از عمان به تمامی بنادر ایران؛ همراه با هماهنگی و پیگیری مراحل حمل.",
  "- از چین به امارات و عمان",
  "- از عمان به تمامی بنادر ایران",
  "- هماهنگی و پیگیری حمل",
];

/** «درباره ما» from the owner's brand book and brochure (تابستان ۱۴۰۵) */
const aboutCopy = page({
  title: "درباره تابان خودرو",
  lede: "تابان خودرو، نمایندگی رسمی تویوتا، لکسوس و نیسان، با تکیه بر تجربه و تخصص مسیری مطمئن و شفاف برای دسترسی به خودروهای معتبر جهانی فراهم می‌کند.",
  updated: "",
  body: [
    ...ABOUT_STORY,
    "",
    "## مأموریت",
    "ساختن مسیری مطمئن، حرفه‌ای و شفاف برای دسترسی بازار ایران به خودروهای معتبر و باکیفیت جهانی؛ از انتخاب و تأمین خودرو تا عرضه، تحویل و خدمات پس از فروش.",
    "",
    "## چشم‌انداز",
    "می‌خواهیم یکی از معتبرترین و اثرگذارترین مجموعه‌های صنعت خودرو در ایران باشیم؛ با پیوندی پایدار میان بازارهای جهانی، مناطق آزاد و سرزمین اصلی.",
    "",
    "## ارزش‌های ما",
    "اعتبار و اعتماد: به تعهداتمان پایبندیم و روابطی پایدار با مشتریان و شرکای تجاری می‌سازیم.",
    "",
    "کیفیت: در انتخاب خودرو، خدمات و اجرا، کیفیت استاندارد همیشگی ماست، نه یک انتخاب.",
    "",
    "شفافیت: از تأمین تا فروش، فرایندها را روشن و قابل‌فهم نگه می‌داریم.",
    "",
    "مشتری‌مداری: فروش پایان کار نیست؛ آغاز یک رابطه بلندمدت است.",
  ],
  metaDescription:
    "تابان خودرو، پلی میان مناطق آزاد و سرزمین اصلی: واردات، فروش و خدمات پس از فروش خودروهای معتبر جهانی، با اعتبار، کیفیت و شفافیت.",
});

/** «درباره ما» as it shipped before the brand book: a saved row still holding it reads as today's */
const ABOUT_BEFORE = {
  lede: "فروش خودروی صفر کیلومتر و خدمات پس از فروش، با قیمت شفاف و مسیر روشن خرید.",
  body: [
    "تابان خودرو خودروی صفر کیلومتر می‌فروشد و پس از تحویل هم کنار مالک می‌ماند: از سرویس دوره‌ای و تعمیر تا تأمین قطعه و امداد جاده‌ای.",
    "",
    "## آنچه انجام می‌دهیم",
    "فروش نقدی و اقساطی خودروهای صفر کیلومتر، با قیمت، شرایط فروش و مدارک لازم هر مدل که پیش از مراجعه در سایت می‌بینید.",
    "",
    "پذیرش سرویس و تعمیر در شعبه‌ها، با رزرو نوبت و پیگیری وضعیت خودرو در پنل مشتریان.",
    "",
    "## چطور کار می‌کنیم",
    "پیش از هر تصمیم، عددها روشن است: قیمت نقدی، برآورد اقساط و هزینه‌ی خدمات را پیش از ثبت درخواست می‌بینید و شرایط نهایی در قرارداد درج می‌شود.",
    "",
    "## تماس با ما",
    "برای خرید یا مشاوره، فرم درخواست مشاوره را پر کنید یا با نزدیک‌ترین شعبه تماس بگیرید؛ تلفن‌ها، نشانی و ساعت کاری شعبه‌ها در صفحه «تماس با ما» آمده است.",
  ].join("\n"),
  metaDescription:
    "تابان خودرو: فروش خودروی صفر کیلومتر، اقساط، سرویس و تعمیر، قطعه و امداد جاده‌ای در یک مسیر روشن.",
};

/** The icons staff may give a button of the mobile bottom bar; the site maps each value to its hugeicon */
export const mobileBarIcons = [
  { value: "car", label: "خودرو" },
  { value: "search", label: "ذره‌بین (جستجو)" },
  { value: "compare", label: "دو فلش (مقایسه)" },
  { value: "consult", label: "پیام (مشاوره)" },
  { value: "panel", label: "آدمک (حساب کاربری)" },
  { value: "home", label: "خانه" },
  { value: "phone", label: "گوشی تلفن" },
  { value: "branches", label: "نشانگر نقشه (شعب)" },
  { value: "calculator", label: "ماشین‌حساب" },
] as const;
export type MobileBarIcon = (typeof mobileBarIcons)[number]["value"];

/** one button of the mobile bottom bar: its link (empty hides the button) and its icon */
const barButton = (n: number, link: string, icon: MobileBarIcon): [SiteField, SiteField] => [
  {
    label: `دکمه ${faPlain.format(n)}: عنوان | /مسیر (خالی: دکمه نمایش داده نمی‌شود)`,
    kind: "links",
    max: 120,
    items: 1,
    initial: link,
    optional: true,
  },
  {
    label: `دکمه ${faPlain.format(n)}: آیکن`,
    kind: "choice",
    max: 12,
    initial: icon,
    options: mobileBarIcons,
  },
];
const [item1, item1Icon] = barButton(1, "خانه | /", "car");
const [item2, item2Icon] = barButton(2, "خودروها | /vehicles", "search");
const [item3, item3Icon] = barButton(3, "مقایسه | /compare", "compare");
const [item4, item4Icon] = barButton(4, "مشاوره | /#consult", "consult");
const [item5, item5Icon] = barButton(5, "پنل من | /panel", "panel");

/**
 * A trust seal of the footer (e.g. e-namad, samandehi): the issuer's page as a single link and
 * the issuer's own image URL, both from the code the issuer gives; no HTML, so nothing runs.
 */
const sealLink = (n: number, initial = ""): SiteField => ({
  label: `نماد ${faPlain.format(n)} در پاورقی: «عنوان | https://صفحه‌ی نماد»، مثلاً «نماد اعتماد الکترونیکی | https://trustseal.enamad.ir/?id=…» (خالی: ${initial ? "نماد پیش‌فرض" : "نمایش داده نمی‌شود"})`,
  kind: "links",
  max: 500,
  items: 1,
  initial,
  optional: true,
});
const sealImage = (n: number, initial = ""): SiteField =>
  optional(
    text(
      `نماد ${faPlain.format(n)}: نشانی تصویر نماد (https://…)، همان که صادرکننده‌ی نماد در کدش داده است`,
      initial,
      500,
    ),
  );
const SEAL_IMAGE = /^https:\/\/[^\s"'<>]+$/;

/**
 * Taban Khodro's e-namad seal, the first footer seal's default: a row saved while that seal was
 * still empty reads as it too, so the seal needs no re-save in the panel (set another seal 1 to replace it)
 */
const ENAMAD_ID = "id=565979&Code=nOkXMUwGlqO7Rpvc9N3DcyfKMuVi88kF";
const ENAMAD_LINK = `نماد اعتماد الکترونیکی | https://trustseal.enamad.ir/?${ENAMAD_ID}`;
const ENAMAD_IMAGE = `https://trustseal.enamad.ir/logo.aspx?${ENAMAD_ID}`;

/**
 * The seeded placeholder hotline («021-00000000»): a row saved before the hotline became
 * optional still holds it, and it is read (and saved) as no number, so the site hides the
 * hotline instead of offering a number nobody answers.
 */
const PLACEHOLDER_PHONE = "02100000000";

/** the shipping site's questions, one «سؤال | پاسخ» per line (see faqItems) */
const SHIPPING_FAQ = [
  "کرایه حمل چه زمانی پرداخت می‌شود؟ | بخشی از کرایه هنگام قرارداد به‌عنوان پیش‌پرداخت دریافت می‌شود و مانده‌ی آن در مقصد، پیش از تحویل خودرو و مدارک، تسویه می‌شود.",
  "حمل خودرو چقدر طول می‌کشد؟ | مدت تقریبی هر مسیر در بخش مسیرها آمده و از روز تحویل خودرو در مبدأ حساب می‌شود؛ شرایط جوی، ازدحام بندر و تشریفات گمرکی ممکن است آن را طولانی‌تر کند.",
  "هزینه‌های گمرک و ترخیص با کیست؟ | گمرک، ترخیص، مالیات، عوارض، انبارداری، پارکینگ و دیگر هزینه‌های قانونی بر عهده‌ی صاحب خودرو است.",
  "وضعیت خودرو هنگام تحویل چطور ثبت می‌شود؟ | در هر تحویل، صورت‌جلسه‌ای با چک‌لیست وضعیت خودرو در حضور طرفین تنظیم می‌شود و نتیجه‌ی آن در صفحه‌ی پیگیری دیده می‌شود.",
  "بعد از رسیدن خودرو چه مدت برای تحویل گرفتن فرصت دارم؟ | پس از اعلام ورود، هفت روز فرصت تسویه و تحویل گرفتن خودرو دارید؛ پس از آن، طبق قرارداد وجه التزام تعلق می‌گیرد.",
  "چطور وضعیت خودرو را پیگیری کنم؟ | با کد پیگیری‌ای که پس از ثبت درخواست پیامک می‌شود، در صفحه‌ی «پیگیری خودرو» همه‌ی مراحل و تاریخ‌ها را ببینید.",
];

/** a menu group is shown only with both its title and its links; one without the other is refused */
const groupsComplete =
  (groups: readonly (readonly [title: string, links: string, name: string])[]) =>
  (values: Record<string, string>) => {
    for (const [title, links, name] of groups) {
      if (Boolean(values[title]) !== Boolean(values[links])) {
        return `${name}: عنوان و پیوندها را با هم پر کنید یا هر دو را خالی بگذارید.`;
      }
    }
    return null;
  };

export const siteBlocks = {
  hero: {
    title: "هیرو صفحه اصلی",
    description:
      "پیام اول صفحه اصلی روی صحنه‌ی پرده‌برداری از خودرو، با دو دکمه «خودروها» و «مشاوره»، و تیتری که پس از کنار رفتن پرده می‌آید.",
    fields: {
      image: image(
        "عکس پس‌زمینه (خودروی تیره روی زمینه تیره؛ سایت آن را تاریک می‌کند و نور روی خودرو می‌چرخد)",
        stockPhotoUrl("photo-1606590526453-9b8ee4569a70"),
      ),
      flip: {
        label: "جهت عکس",
        kind: "choice",
        max: 10,
        initial: "mirror",
        options: [
          { value: "mirror", label: "قرینه شود (جلوی خودرو باید به سمت چپ بیفتد)" },
          { value: "none", label: "همان‌طور که هست" },
        ],
      },
      titleTop: text("سطر اول تیتر", "خودروی نو،", 24),
      titleBottom: text("سطر دوم تیتر", "زیر نورِ تابان", 24),
      lede: paragraph(
        "متن زیر تیتر",
        "خودروی صفر کیلومتر با قیمت شفاف، شرایط روشن اقساط و خدمات مالکیت، همه در یک‌جا",
        180,
      ),
      primaryCta: text("دکمه اصلی (به فهرست خودروها)", "دیدن خودروها", 24),
      secondaryCta: text("دکمه دوم (به فرم مشاوره)", "درخواست مشاوره", 24),
      revealTitle: text("تیتر پایان پرده‌برداری", "بی‌پرده، صفر کیلومتر", 24),
      revealText: paragraph(
        "متن پایان پرده‌برداری",
        "قیمت نقدی، برآورد اقساط و زمان تحویل هر مدل را پیش از تماس ببینید.",
        140,
      ),
    },
  },
  homeShowcase: {
    title: "صفحه اصلی: ویترین و مسیر خرید",
    description: "ویترین خودروها، سه قدمِ انتخاب و سربرگ فرم درخواست مشاوره.",
    fields: {
      showcaseTitle: text("عنوان ویترین", "ویترینِ امروز"),
      showcaseLede: paragraph(
        "متن ویترین",
        "قیمت نقدی و برآورد اقساط را کنار مشخصات اصلی ببینید؛ روی هر مدل بروید تا خودش را نشان دهد.",
        200,
      ),
      showcaseLink: text("لینک زیر ویترین", "مشاهده همه خودروها", 40),
      stepsTitle: text("عنوان سه قدم", "سه قدم تا انتخاب درست"),
      stepsLede: paragraph(
        "متن سه قدم",
        "خودروها را بشناسید، کنار هم بگذارید و اگر باز هم شک داشتید، مشاوره بگیرید.",
        200,
      ),
      step1Title: text("قدم ۱ (لینک به خودروها): عنوان", "معرفی خودروها", 40),
      step1Text: text("قدم ۱: توضیح", "مشخصات کامل، گالری، شرایط فروش و زمان تحویل هر مدل.", 120),
      step2Title: text("قدم ۲ (لینک به مقایسه): عنوان", "مقایسه خودروها", 40),
      step2Text: text(
        "قدم ۲: توضیح",
        "تا سه مدل را کنار هم بگذارید: قیمت، قسط، مشخصات فنی و زمان تحویل.",
        120,
      ),
      step3Title: text("قدم ۳ (لینک به فرم مشاوره): عنوان", "مشاوره رایگان", 40),
      step3Text: text(
        "قدم ۳: توضیح",
        "بدون ورود به حساب؛ شماره و موضوع را بگویید تا کارشناس فروش تماس بگیرد.",
        120,
      ),
      consultTitle: text("عنوان فرم مشاوره", "درخواست مشاوره رایگان"),
      consultIntro: paragraph(
        "متن فرم مشاوره",
        "بدون ورود به حساب؛ فقط شماره تماس و موضوع را بگویید تا کارشناس فروش تماس بگیرد.",
        200,
      ),
    },
  },
  homeTools: {
    title: "صفحه اصلی: ابزارها و اعتماد",
    description: "سربرگ ماشین‌حساب اقساط، کارت معاوضه و نوار چهار نشان اعتماد.",
    fields: {
      toolsTitle: text("عنوان ابزارها", "قبل از تصمیم، عددها را بررسی کنید"),
      toolsLede: paragraph(
        "متن ابزارها",
        "برآورد اولیه رایگان است و برای دیدن نتیجه به ورود نیاز ندارید.",
        200,
      ),
      tradeTitle: text("عنوان کارت معاوضه", "ارزش خودروی فعلی را بدانید"),
      tradeText: paragraph(
        "متن کارت معاوضه",
        "قیمت معاوضه پس از کارشناسی حضوری مشخص می‌شود؛ مشخصات خودروی فعلی را بفرستید تا کارشناس فروش نوبت بازدید هماهنگ کند.",
        300,
      ),
      tradeCta: text("دکمه کارت معاوضه", "درخواست کارشناسی معاوضه", 40),
      trustTitle: text("عنوان نوار اعتماد", "اعتماد باید قابل بررسی باشد"),
      // the first badge used to promise a used-car inspection report; Taban sells new cars only
      trust1Title: replacing(text("نشان ۱ (سپر): عنوان", "گارانتی رسمی", 40), "کارشناسی مستند"),
      trust1Detail: replacing(
        text("نشان ۱: توضیح", "ضمانت سازنده برای همه مدل‌های صفر کیلومتر", 80),
        "گزارش وضعیت بدنه و فنی پیش از خرید",
      ),
      trust2Title: text("نشان ۲ (آچار): عنوان", "تکنسین‌های آموزش‌دیده", 40),
      trust2Detail: text("نشان ۲: توضیح", "سرویس مطابق دستورالعمل سازنده", 80),
      trust3Title: text("نشان ۳ (جعبه): عنوان", "قطعات اصلی", 40),
      trust3Detail: text("نشان ۳: توضیح", "استعلام سازگاری و ضمانت قطعه", 80),
      trust4Title: text("نشان ۴ (کیف پول): عنوان", "قیمت شفاف", 40),
      trust4Detail: text("نشان ۴: توضیح", "نمایش هزینه پیش از ثبت درخواست", 80),
    },
  },
  finance: {
    title: "اقساط و ماشین‌حساب",
    description:
      "یک نرخ سود برای کل سایت (ماشین‌حساب‌ها و طرح‌هایی که نرخ جدا ندارند) و بازه ماشین‌حساب اقساط. خود طرح‌های فروش در زبانه «طرح‌های فروش و اقساط» مدیریت می‌شوند.",
    fields: {
      annualRate: whole("نرخ سود سالانه اقساط (درصد)", 23, [0, 60]),
      disclaimer: optional(
        paragraph(
          "توضیح زیر نتیجه محاسبه، پس از «محاسبه بر اساس نرخ سالانه …٪» (خالی: نمایش داده نمی‌شود)",
          "برآورد اولیه است؛ مبلغ نهایی قسط پس از اعتبارسنجی در قرارداد درج می‌شود.",
          200,
        ),
      ),
      monthOptions: {
        label: monthsText,
        kind: "numbers",
        max: 40,
        initial: "12, 24, 36, 48",
        range: [1, 120],
      },
      monthDefault: whole("مدت پیش‌فرض ماشین‌حساب (یکی از مدت‌های بالا)", 24, [1, 120]),
      downMin: whole("ماشین‌حساب صفحه خودرو: کمترین درصد پیش‌پرداخت", 20, [0, 100]),
      downMax: whole("ماشین‌حساب صفحه خودرو: بیشترین درصد پیش‌پرداخت", 70, [0, 100]),
      downDefault: whole("ماشین‌حساب صفحه خودرو: درصد پیش‌پرداخت پیش‌فرض", 40, [0, 100]),
    },
    check: (values) => {
      const min = Number(values.downMin);
      const max = Number(values.downMax);
      const initial = Number(values.downDefault);
      if (min >= max) return "کمترین درصد پیش‌پرداخت باید از بیشترین کمتر باشد.";
      if (initial < min || initial > max) {
        return "درصد پیش‌پرداخت پیش‌فرض باید بین کمترین و بیشترین باشد.";
      }
      if (!numbers(values.monthOptions ?? "").includes(Number(values.monthDefault))) {
        return "مدت پیش‌فرض باید یکی از مدت‌های بازپرداخت باشد.";
      }
      return null;
    },
  },
  payment: {
    title: "پرداخت کارت‌به‌کارت، واریز و جریمه اقساط",
    description:
      "حسابی که مشتریان در پنل برای کارت‌به‌کارت یا واریز بیعانه، مانده سفارش، قسط، فاکتور و قطعه می‌بینند (پرداخت پس از تأیید کارشناس در «صندوق و تراکنش‌ها» ثبت می‌شود)، و قاعده جریمه دیرکرد اقساط. بدون شماره کارت و شبا، پنل فقط پرداخت در شعبه را پیشنهاد می‌کند.",
    fields: {
      bankName: optional(text("نام بانک", "", 40)),
      holder: optional(text("صاحب حساب (همان نامی که مشتری هنگام انتقال می‌بیند)", "", 80)),
      cardNumber: optional(text("شماره کارت ۱۶ رقمی", "", 30)),
      sheba: optional(text("شماره شبا (IR و ۲۴ رقم)", "", 40)),
      accountNumber: optional(text("شماره حساب", "", 30)),
      instructions: optional(
        paragraph(
          "راهنمای زیر اطلاعات حساب (خالی: نمایش داده نمی‌شود)",
          "پس از واریز، کد پیگیری و تصویر رسید را همین‌جا ثبت کنید؛ پرداخت پس از تطبیق با حساب تأیید می‌شود و نتیجه را همین‌جا می‌بینید.",
          300,
        ),
      ),
      lateFeePercent: whole(
        "جریمه دیرکرد قسط: درصد مبلغ قسط برای هر ماه تأخیر، روزشمار (۰: بدون جریمه)",
        0,
        [0, 10],
      ),
      lateFeeGraceDays: whole("مهلت بدون جریمه پس از سررسید (روز)", 0, [0, 30]),
    },
    check: (values) => {
      const card = latinDigits(values.cardNumber ?? "").replace(/[\s-]/g, "");
      if (card && !/^\d{16}$/.test(card)) return "شماره کارت باید ۱۶ رقم باشد.";
      const sheba = latinDigits(values.sheba ?? "")
        .replace(/[\s-]/g, "")
        .toUpperCase();
      if (sheba && !/^IR\d{24}$/.test(sheba)) return "شماره شبا باید با IR و ۲۴ رقم باشد.";
      if ((card || sheba) && !values.holder) return "نام صاحب حساب را هم بنویسید.";
      return null;
    },
  },
  homeStories: {
    title: "صفحه اصلی: نظرات، مجله و شعب",
    description: "سربرگ بخش‌های نظرات مشتریان، مجله خودرو، تازه‌ها و شعب.",
    fields: {
      reviewsTitle: text("عنوان نظرات", "تجربه‌ی خرید و خدمات، جدا و روشن"),
      reviewsLede: text("متن نظرات", "نظر مشتریان تأییدشده، همان‌طور که نوشته‌اند.", 160),
      guidesTitle: text("عنوان مجله", "راهنمای خرید و نگهداری"),
      guidesLede: text("متن مجله", "پاسخ‌های کاربردی برای تصمیم بهتر و هزینه کمتر.", 160),
      newsTitle: text("عنوان تازه‌ها", "تازه‌های تابان خودرو"),
      branchesTitle: text("عنوان شعب", "نزدیک‌ترین شعبه را انتخاب کنید"),
      branchesLede: paragraph(
        "متن شعب",
        "ساعت فروش و خدمات هر شعبه جدا نمایش داده می‌شود؛ رزرو نوبت سرویس از پنل مشتریان انجام می‌شود.",
        200,
      ),
      branchesCta: text("لینک رزرو نوبت", "رزرو نوبت سرویس", 40),
    },
  },
  vehiclesPage: {
    title: "صفحه خودروها",
    description: "سربرگ صفحه «همه خودروها»، سه کاشی نوع سوخت و نوار دعوت به محاسبه اقساط.",
    fields: {
      title: text("عنوان صفحه", "همه خودروها", 40),
      lede: paragraph(
        "متن زیر عنوان",
        "همه مدل‌ها صفر کیلومتر و با گارانتی رسمی هستند. از نوع سوخت شروع کنید یا مستقیم فیلتر کنید.",
        200,
      ),
      fuelHeading: text("عنوان کاشی‌های سوخت", "انتخاب بر اساس نوع سوخت"),
      electricImage: image("عکس کاشی برقی", stockPhotoUrl("photo-1555215695-3004980ad54e")),
      electricBlurb: text("متن کاشی برقی", "بدون بنزین، با برد مطمئن برای شهر و سفر", 80),
      hybridImage: image("عکس کاشی هیبریدی", stockPhotoUrl("photo-1568605117036-5fe5e7bab0b7")),
      hybridBlurb: text("متن کاشی هیبریدی", "مصرف کم، بدون نگرانی شارژ", 80),
      gasolineImage: image("عکس کاشی بنزینی", stockPhotoUrl("photo-1494976388531-d1058494cdd8")),
      gasolineBlurb: text("متن کاشی بنزینی", "انتخاب‌های متنوع برای هر بودجه", 80),
      financeTitle: text("عنوان نوار اقساط", "قبل از مراجعه، قسط ماهانه را حساب کنید", 80),
      financeText: text("متن نوار اقساط", "برآورد اولیه رایگان است و به ورود نیاز ندارد.", 120),
      // the button opens the buying guide, not a calculator (that sits on each car's page)
      financeCta: replacing(text("دکمه نوار اقساط", "شرایط اقساط", 24), "محاسبه اقساط"),
      metaDescription: paragraph(
        "توضیح صفحه برای گوگل",
        "همه خودروهای صفر کیلومتر تابان خودرو با فیلتر نوع سوخت، بدنه و قیمت.",
        160,
      ),
    },
  },
  vehiclePage: {
    title: "صفحه هر خودرو",
    description: `متن‌های مشترک همه صفحه‌های خودرو: پیوندهای راهنمای خرید، توضیح زیر قیمت، «شرایط فروش و مراحل خرید»، کارت خرید کنار صفحه، فرم و پیام‌های درخواست خرید، نوار پایین صفحه، توضیح گوگل و پیام خودروی پیدانشده. ${slotsHelp}`,
    fields: {
      priceNote: slotted(
        optional(
          paragraph(
            "توضیح زیر قیمت و تاریخ به‌روزرسانی آن، بالای صفحه (خالی: نمایش داده نمی‌شود)",
            "قیمت فعلی فهرست برای خرید نقدی است و با تغییر نرخ‌ها ممکن است عوض شود. مبلغ قطعی و آنچه در آن هست در قرارداد درج می‌شود.",
            240,
          ),
        ),
      ),
      priceInquiry: text(
        "متنی که به‌جای قیمت می‌آید، برای پلاکی که قیمتش در مدیریت محتوا «استعلام قیمت» است",
        "جهت استعلام قیمت درخواست مشاوره ثبت کنید یا تماس بگیرید.",
        160,
      ),
      guidePlatesLink: optional(
        text(
          "پیوند راهنمای خرید زیر قیمت، برای مدلی که با دو پلاک فروخته می‌شود (خالی: نمایش داده نمی‌شود)",
          "تفاوت پلاک ملی و منطقه آزاد چیست؟",
          60,
        ),
      ),
      guideLink: optional(
        text(
          "پیوند راهنمای خرید زیر قیمت، برای مدلی با یک پلاک (خالی: نمایش داده نمی‌شود)",
          "راهنمای خرید و انواع پلاک",
          60,
        ),
      ),
      termsIntro: slotted(
        optional(
          paragraph(
            "متن آغاز «شرایط فروش و مراحل خرید»؛ زمان تحویل را قطعی ننویسید، مگر شعبه به آن متعهد باشد (خالی: نمایش داده نمی‌شود)",
            "تحویل {خودرو} از {شعبه} حدود {روز} روز کاری پس از عقد قرارداد پیش‌بینی می‌شود و زمان قطعی تحویل در قرارداد درج می‌شود. همه طرح‌ها شامل {گارانتی} هستند.",
            300,
          ),
        ),
      ),
      purchaseSteps: list("مراحل خرید (هر خط یک قدم)", [
        "ثبت درخواست آنلاین یا تماس با شعبه",
        "تماس کارشناس فروش برای هماهنگی",
        "عقد قرارداد و پرداخت پیش‌پرداخت در شعبه",
        "تحویل خودرو با پلاک و بیمه شخص ثالث",
      ]),
      requiredDocuments: list("مدارک لازم (هر خط یک مدرک)", [
        "کارت ملی و شناسنامه",
        "چک صیادی بنفش (فقط برای خرید اقساطی)",
        "پرینت گردش حساب سه‌ماهه (خرید اقساطی)",
        "وکالت‌نامه در صورت خرید برای شخص دیگر",
      ]),
      guideTermsLink: optional(
        text(
          "پیوند راهنمای خرید زیر «مراحل خرید» و «مدارک لازم» (خالی: نمایش داده نمی‌شود)",
          "راهنمای کامل خرید: پرداخت، مدارک و تفاوت دو پلاک",
          60,
        ),
      ),
      asideDelivery: slotted(
        optional(
          text(
            "کارت خرید کنار صفحه: سطر زمان تحویل (خالی: نمایش داده نمی‌شود)",
            "تحویل تقریبی {روز} روز کاری پس از قرارداد",
            80,
          ),
        ),
      ),
      asideBranch: slotted(
        optional(
          text(
            "کارت خرید کنار صفحه: سطر شعبه، فقط برای مدل موجود (خالی: نمایش داده نمی‌شود)",
            "عرضه در {شعبه}",
            80,
          ),
        ),
      ),
      anyBranch: text("آنچه {شعبه} برای مدلی بی‌شعبه‌ی مشخص می‌شود", "شعب تابان خودرو", 40),
      unavailableNotice: slotted(
        optional(
          paragraph(
            "پیام بالای فرم درخواست، برای مدلی که فعلاً موجود نیست (خالی: نمایش داده نمی‌شود)",
            "این خودرو فعلاً برای فروش موجود نیست. شماره‌تان را بگذارید تا با موجود شدن یا برای پیشنهاد خودروی مشابه با شما تماس بگیریم.",
            240,
          ),
        ),
      ),
      requestFollowUp: slotted(
        optional(
          text(
            "پیام پس از ثبت درخواست خرید (خالی: فقط «درخواست شما ثبت شد»)",
            "کارشناس فروش برای هماهنگی با شما تماس می‌گیرد.",
            160,
          ),
        ),
      ),
      unavailableFollowUp: slotted(
        optional(
          text(
            "پیام پس از ثبت درخواست برای مدلی که فعلاً موجود نیست (خالی: فقط «درخواست شما ثبت شد»)",
            "با موجود شدن این خودرو یا برای پیشنهاد خودروی مشابه با شما تماس می‌گیریم.",
            160,
          ),
        ),
      ),
      ctaTitle: slotted(text("نوار پایین صفحه: تیتر", "{خودرو} را از نزدیک ببینید", 80)),
      ctaText: slotted(
        optional(
          paragraph(
            "نوار پایین صفحه: متن (خالی: نمایش داده نمی‌شود)",
            "برای بازدید از {خودرو} در {شعبه}، پیش از مراجعه با شعبه هماهنگ کنید.",
            200,
          ),
        ),
      ),
      ctaLink: {
        label: "نوار پایین صفحه: دکمه، «عنوان | /مسیر» (خالی: دکمه نمایش داده نمی‌شود)",
        kind: "links",
        max: 120,
        items: 1,
        initial: "آدرس شعبه‌ها | /#branches",
        optional: true,
      },
      metaDescription: slotted(
        optional(
          text(
            "توضیح صفحه برای گوگل: جمله‌ی پس از شعار و قیمت مدل (خالی: نمایش داده نمی‌شود)",
            "مشخصات فنی، امکانات، شرایط فروش و اقساط در تابان خودرو.",
            120,
          ),
        ),
      ),
      notFoundTitle: text(
        "خودروی پیدانشده (پیوند قدیمی یا خودروی حذف‌شده): تیتر",
        "این خودرو پیدا نشد",
        60,
      ),
      notFoundText: paragraph(
        "خودروی پیدانشده: متن",
        "ممکن است لینک قدیمی باشد یا خودرو از سایت حذف شده باشد. خودروهای موجود را ببینید یا مشاوره بگیرید.",
        200,
      ),
    },
  },
  comparePage: {
    title: "صفحه مقایسه",
    description: "سربرگ صفحه مقایسه خودروها و دعوت به مشاوره زیر جدول.",
    fields: {
      title: text("عنوان صفحه", "مقایسه خودروها", 40),
      lede: paragraph(
        "متن زیر عنوان",
        "تا ۳ خودرو را کنار هم بگذارید و قیمت، قسط تخمینی و مشخصات فنی را سطر به سطر ببینید.",
        200,
      ),
      consultCta: text("لینک مشاوره زیر جدول", "در انتخاب شک دارید؟ درخواست مشاوره", 60),
      metaDescription: paragraph(
        "توضیح صفحه برای گوگل",
        "تا سه خودرو را کنار هم بگذارید و قیمت، اقساط و مشخصات فنی را مقایسه کنید.",
        160,
      ),
    },
  },
  aboutPage: {
    title: "صفحه درباره ما",
    description:
      "صفحه «درباره ما» (/about) که از سربرگ و پاورقی باز می‌شود. در متن صفحه، بندی که با «عنوان: » شروع شود عنوانش پررنگ می‌آید (زیرمجموعه‌ها و ارزش‌ها).",
    fields: {
      title: aboutCopy.title,
      tagline: optional(
        text(
          "شعار برند، درشت بالای صفحه (خالی: عنوان صفحه جایش می‌آید)",
          "پلی میان مناطق آزاد تا سرزمین اصلی",
          60,
        ),
      ),
      lede: replacing(aboutCopy.lede, ABOUT_BEFORE.lede),
      updated: aboutCopy.updated,
      body: replacing(
        aboutCopy.body,
        ABOUT_BEFORE.body,
        aboutCopy.body.initial.replace(
          "## مأموریت",
          `${ABOUT_SUBSIDIARIES_BEFORE.join("\n")}\n\n## مأموریت`,
        ),
      ),
      servicesTitle: text("عنوان بخش خدمات", "خدمات یکپارچه خودرو", 60),
      services: paragraph(
        "خدمات: هر زیرمجموعه با «## نام» شروع می‌شود؛ خط بعد زیرعنوان، خط‌های بعدی متن و خطی که با «- » شروع شود یک مورد از فهرست خدمات است",
        ABOUT_SERVICES.join("\n"),
        3000,
      ),
      closing: optional(
        paragraph(
          "جمله پایانی، بالای دکمه‌های پایین صفحه (خالی: نمایش داده نمی‌شود)",
          "ما اینجا هستیم تا این مسیر را هموار کنیم؛ از کیش و سرزمین اصلی، از ایران تا جهان و از یک انتخاب تا یک تجربه ماندگار.",
          200,
        ),
      ),
      metaDescription: replacing(aboutCopy.metaDescription, ABOUT_BEFORE.metaDescription),
    },
  },
  termsPage: {
    title: "صفحه شرایط استفاده",
    description: `صفحه «شرایط استفاده» (/terms) که از پاورقی باز می‌شود. ${legalReview}`,
    fields: page({
      title: "شرایط استفاده",
      lede: "استفاده از سایت و پنل مشتریان تابان خودرو یعنی پذیرفتن شرایط زیر.",
      updated: "آخرین به‌روزرسانی: مهر ۱۴۰۵",
      body: [
        "## قیمت‌ها و مشخصات",
        "قیمت، مشخصات فنی و زمان تحویل خودروها بر اساس آخرین اطلاعات در دسترس نمایش داده می‌شود و ممکن است تغییر کند. مبلغ و شرایط نهایی هر خرید همانی است که در قرارداد فروش درج می‌شود.",
        "",
        "## برآورد اقساط",
        "نتیجه ماشین‌حساب اقساط برآوردی اولیه است و تعهدی برای پرداخت تسهیلات نیست؛ مبلغ نهایی قسط پس از اعتبارسنجی مشخص می‌شود.",
        "",
        "## درخواست‌ها و نوبت‌ها",
        "ثبت درخواست مشاوره، خرید یا نوبت سرویس در سایت به معنای بستن قرارداد نیست؛ کارشناسان ما برای هماهنگی با شما تماس می‌گیرند.",
        "",
        "## حساب کاربری",
        "ورود به پنل مشتریان با کد یک‌بارمصرفی است که به شماره همراه شما پیامک می‌شود. این کد را به هیچ‌کس، حتی کارکنان تابان خودرو، ندهید.",
        "",
        "## محتوای سایت",
        "متن‌ها، تصاویر و نشان تابان خودرو متعلق به تابان خودرو یا صاحبان آن‌هاست و استفاده تجاری از آن‌ها بدون اجازه کتبی مجاز نیست.",
        "",
        "## تغییر شرایط",
        "این شرایط ممکن است به‌روزرسانی شود؛ نسخه جاری همیشه در همین صفحه در دسترس است.",
      ],
      metaDescription:
        "شرایط استفاده از سایت و پنل مشتریان تابان خودرو: قیمت‌ها، برآورد اقساط، درخواست‌ها و حساب کاربری.",
    }),
  },
  privacyPage: {
    title: "صفحه حریم خصوصی",
    description: `صفحه «حریم خصوصی» (/privacy) که از پاورقی باز می‌شود. ${legalReview}`,
    fields: withRetiredBody(
      page({
        title: "حریم خصوصی",
        lede: "چه اطلاعاتی از شما می‌گیریم، برای چه و چه کسی به آن دسترسی دارد.",
        updated: "آخرین به‌روزرسانی: مهر ۱۴۰۵",
        body: [
          "## اطلاعاتی که می‌گیریم",
          "نام و شماره همراهی که در فرم‌های مشاوره، خرید یا نوبت سرویس وارد می‌کنید و برای مالکان، مشخصات خودرو و سوابق سرویس و تعمیر آن.",
          "",
          "## استفاده از اطلاعات",
          "این اطلاعات فقط برای پاسخ به درخواست شما، هماهنگی خرید و خدمات، فرستادن پیامک‌های مربوط به همان درخواست‌ها و نگهداری سوابق خودروی شما به کار می‌رود.",
          "",
          "## دسترسی به اطلاعات",
          "اطلاعات شما فقط در دسترس کارکنانی است که برای انجام درخواستتان به آن نیاز دارند و جز به ارائه‌دهندگان خدماتی که برای همان کار لازم‌اند (مانند سامانه پیامک) یا به حکم قانون، در اختیار کسی گذاشته نمی‌شود.",
          "",
          "## کوکی و حافظه مرورگر",
          "سایت برای نگه داشتن ورود شما به پنل و پوسته‌ای که انتخاب کرده‌اید از کوکی و حافظه مرورگر استفاده می‌کند.",
          "",
          "## حقوق شما",
          PRIVACY_RIGHTS,
        ],
        metaDescription:
          "سیاست حریم خصوصی تابان خودرو: اطلاعاتی که می‌گیریم، کاربرد آن و دسترسی به آن.",
      }),
      (body) => body.replace(PRIVACY_RIGHTS, PRIVACY_RIGHTS_BEFORE),
    ),
  },
  accessibilityPage: {
    title: "صفحه دسترس‌پذیری",
    description: "صفحه «دسترس‌پذیری» (/accessibility) که از پاورقی باز می‌شود.",
    fields: page({
      title: "دسترس‌پذیری",
      lede: "می‌خواهیم همه بتوانند خودروها را بشناسند، مقایسه کنند و خدمات را پیگیری کنند.",
      updated: "",
      body: [
        "## آنچه رعایت می‌کنیم",
        "صفحه‌ها راست‌به‌چپ و با عنوان‌بندی منظم ساخته شده‌اند تا با صفحه‌خوان‌ها خوانا باشند. پیوندها و دکمه‌ها با صفحه‌کلید در دسترس‌اند و جای تمرکز روی آن‌ها دیده می‌شود.",
        "",
        "اگر در تنظیمات دستگاه «کاهش حرکت» را انتخاب کرده باشید، حرکت‌های صفحه اصلی کنار می‌روند و محتوا ثابت نمایش داده می‌شود. بدون آن هم نوار متحرک صفحه اصلی دکمه «توقف حرکت نوار» دارد و نور بالای صفحه پس از چند ثانیه می‌ایستد. پوسته تیره هم از دکمه بالای صفحه در دسترس است.",
        "",
        "## اگر مشکلی دیدید",
        "اگر بخشی از سایت برایتان قابل استفاده نیست، از فرم درخواست مشاوره یا با تماس با شعبه به ما خبر دهید تا کارتان را از راه دیگری انجام دهیم و مشکل را برطرف کنیم.",
      ],
      metaDescription: "دسترس‌پذیری سایت تابان خودرو و راه گزارش مشکل.",
    }),
  },
  guidePage: {
    title: "صفحه راهنمای خرید",
    description: `صفحه «راهنمای خرید» (/guide): مراحل خرید، مدارک و تفاوت پلاک ملی و منطقه آزاد. از پاورقی و از صفحه هر خودرو (کنار قیمت و انتخاب پلاک) باز می‌شود. ${legalReview}`,
    fields: page({
      title: "راهنمای خرید",
      lede: "از ثبت درخواست تا تحویل خودرو به زبان ساده: چه مراحلی دارد، چه مدارکی لازم است و پلاک ملی با پلاک منطقه آزاد چه فرقی دارد.",
      updated: "آخرین به‌روزرسانی: مهر ۱۴۰۵",
      body: [
        "## مراحل خرید",
        "۱. درخواست: در صفحه هر خودرو نوع پلاک و طرح پرداخت را انتخاب کنید و درخواست خرید یا مشاوره را ثبت کنید، یا با شعبه تماس بگیرید. ثبت درخواست هزینه‌ای ندارد و تعهدی برای شما نمی‌سازد.",
        "",
        "۲. مشاوره: کارشناس فروش با شما تماس می‌گیرد، قیمت روز، زمان تحویل و طرح پرداخت مناسب را توضیح می‌دهد و وقت مراجعه به شعبه را هماهنگ می‌کند.",
        "",
        "۳. قرارداد: قرارداد فروش را در شعبه امضا می‌کنید. مبلغ نهایی، زمان تحویل و شرایط پرداخت همان است که در قرارداد نوشته می‌شود؛ پیش از امضا آن را با دقت بخوانید و هر سؤالی دارید بپرسید.",
        "",
        "۴. پرداخت: در خرید نقدی مبلغ خودرو طبق قرارداد پرداخت می‌شود. در خرید اقساطی پیش‌پرداخت را می‌پردازید و برای مانده، پس از اعتبارسنجی، چک می‌دهید؛ مبلغ و تعداد قسط‌ها در قرارداد درج می‌شود.",
        "",
        "۵. تحویل: وقتی خودرو آماده شد با شما هماهنگ می‌کنیم و خودرو را همراه مدارکش در شعبه‌ای که در قرارداد آمده تحویل می‌گیرید. پیش از امضای رسید تحویل، خودرو را با دقت ببینید. وضعیت سفارش را تا روز تحویل در پنل مشتریان، بخش «سفارش‌های در راه»، دنبال کنید.",
        "",
        "## مدارک لازم",
        "کارت ملی و شناسنامه خریدار. برای خرید اقساطی چک صیادی و پرینت گردش حساب سه‌ماهه هم لازم است. اگر برای کس دیگری خرید می‌کنید، وکالت‌نامه رسمی بیاورید.",
        "",
        "شماره‌گذاری با پلاک منطقه آزاد ممکن است مدرک دیگری هم بخواهد؛ کارشناس فروش فهرست دقیق را پیش از قرارداد به شما می‌دهد.",
        "",
        "## پلاک ملی یا پلاک منطقه آزاد",
        "برخی خودروها با هر دو پلاک فروخته می‌شوند و هر پلاک قیمت خودش را دارد. پیش از انتخاب، به جایی که خودرو را بیشتر در آن می‌رانید فکر کنید.",
        "",
        "پلاک ملی: خودرو در همه شهرهای کشور بدون محدودیت تردد می‌کند. قیمت آن معمولاً بیشتر است، چون حقوق و عوارض ورود خودرو در آن پرداخت شده است.",
        "",
        "پلاک منطقه آزاد: خودرو برای تردد در همان منطقه آزاد (مانند کیش) شماره‌گذاری می‌شود و قیمت کمتری دارد. بیرون بردن آن از منطقه آزاد تابع مقررات همان منطقه است و معمولاً فقط با مجوز موقت و برای مدت محدود ممکن است.",
        "",
        "تبدیل پلاک منطقه آزاد به پلاک ملی یعنی ترخیص قطعی خودرو و پرداخت حقوق و عوارض گمرکی به نرخ روز. این کار برای همه مدل‌ها مجاز نیست و هزینه آن جدا از قیمت خودروست. اگر فکر می‌کنید بعداً پلاک ملی لازم دارید، پیش از قرارداد از کارشناس فروش بپرسید؛ معمولاً خرید از اول با پلاک ملی ساده‌تر است.",
        "",
        "## قیمت‌ها",
        "قیمتی که کنار هر خودرو می‌بینید قیمت فعلی فهرست است و تاریخ آخرین به‌روزرسانی کنارش آمده است. مبلغ قطعی و آنچه در آن هست در قرارداد درج می‌شود.",
        "",
        "## سؤال دیگری دارید؟",
        "فرم درخواست مشاوره را پر کنید یا با نزدیک‌ترین شعبه تماس بگیرید؛ کارشناس فروش پیش از هر پرداختی پاسخ شما را می‌دهد.",
      ],
      metaDescription:
        "راهنمای خرید خودرو از تابان خودرو: مراحل خرید، مدارک لازم، پرداخت نقدی و اقساطی و تفاوت پلاک ملی و منطقه آزاد.",
    }),
  },
  headerMenu: {
    title: "منوی سربرگ",
    description:
      "منوی بالای سایت: سه گروه کشویی (عنوان گروه و پیوندهایش؛ هر دو خالی یعنی گروه نمایش داده نمی‌شود)، پیوندهای کنار آن‌ها و منوی موبایل. «تماس و شعب»، جستجو و «مشاوره رایگان» همیشه در سربرگ هستند. هر خط یک پیوند: «عنوان | /مسیر»؛ مسیر با / (صفحه‌های سایت، مثلاً /vehicles یا /guide)، https:// (سایت دیگر، در برگه تازه) یا tel: شروع می‌شود؛ /#consult فرم مشاوره را همان‌جا باز می‌کند.",
    fields: {
      group1Title: optional(text("گروه ۱: عنوان", "خرید خودرو", 24)),
      group1Note: optional(text("گروه ۱: توضیح بالای پیوندها (خالی: نمایش داده نمی‌شود)", "", 80)),
      group1Links: redirected(
        optional(
          menu("گروه ۱: پیوندها", [
            "همه خودروها | /vehicles",
            "مقایسه خودروها | /compare",
            "خودروهای برقی | /vehicles?fuel=electric",
            "خودروهای هیبریدی | /vehicles?fuel=hybrid",
            "راهنمای خرید و اقساط | /guide",
          ]),
        ),
        [
          "همه خودروها | /vehicles",
          "مقایسه خودروها | /compare",
          "خودروهای برقی | /vehicles?fuel=electric",
          "خودروهای هیبریدی | /vehicles?fuel=hybrid",
          "شرایط تأمین مالی | /#tools",
          "درخواست مشاوره | /#consult",
        ],
      ),
      group2Title: optional(text("گروه ۲: عنوان", "خدمات مالکیت", 24)),
      group2Note: optional(
        text(
          "گروه ۲: توضیح بالای پیوندها (خالی: نمایش داده نمی‌شود)",
          "خدمات و درخواست‌ها در پنل مشتریان",
          80,
        ),
      ),
      group2Links: optional(
        menu("گروه ۲: پیوندها", [
          "رزرو سرویس | /panel/appointments/new",
          "پیگیری تعمیر | /panel/appointments",
          "سفارش‌های در راه | /panel/orders",
          "امداد جاده‌ای | /panel/requests",
          "استعلام قطعه اصلی | /panel/requests",
          "سوابق و فاکتورها | /panel/history",
        ]),
      ),
      group3Title: optional(text("گروه ۳: عنوان", "درباره ما", 24)),
      group3Note: optional(text("گروه ۳: توضیح بالای پیوندها (خالی: نمایش داده نمی‌شود)", "", 80)),
      // the companies have no pages of their own yet: staff point each line at its site
      group3Links: optional(
        menu("گروه ۳: پیوندها", [
          "درباره تابان خودرو | /about",
          "تابان تاکسی | /about",
          "کشتی‌رانی تابان | /about",
          "تابان رنت | /about",
        ]),
      ),
      links: redirected(
        optional(menu("پیوندهای کنار گروه‌ها، با عنوان کوتاه", ["راهنمای خرید | /guide"], 3)),
        ["تأمین مالی | /#tools", "شعب | /#branches", "مجله خودرو | /#guides"],
        // the default while «درباره ما» was a plain link (now group 3)
        "راهنمای خرید | /guide\nدرباره ما | /about",
      ),
      mobileLinks: redirected(
        menu("منوی موبایل", [
          "همه خودروها | /vehicles",
          "مقایسه خودروها | /compare",
          "مشاوره رایگان | /#consult",
          "راهنمای خرید و اقساط | /guide",
          "خدمات و درخواست‌ها (پنل) | /panel",
          "درباره ما | /about",
        ]),
        [
          "خرید خودرو | /vehicles",
          "مقایسه خودروها | /compare",
          "درخواست مشاوره | /#consult",
          "تأمین مالی | /#tools",
          "راهنماها | /#guides",
          "شعب | /#branches",
          "تماس با ما | /contact",
          "خدمات و درخواست‌ها (پنل) | /panel",
        ],
      ),
    },
    check: groupsComplete([
      ["group1Title", "group1Links", "گروه ۱"],
      ["group2Title", "group2Links", "گروه ۲"],
      ["group3Title", "group3Links", "گروه ۳"],
    ]),
  },
  footerMenu: {
    title: "منوی پاورقی",
    description:
      "چهار ستون پیوند پاورقی (عنوان ستون و پیوندهایش؛ هر دو خالی یعنی ستون نمایش داده نمی‌شود) و پیوندهای ردیف پایین. هر خط یک پیوند: «عنوان | /مسیر»؛ مسیر با / (صفحه‌های سایت، مثلاً /about یا /#branches)، https:// (سایت دیگر، در برگه تازه) یا tel: شروع می‌شود.",
    fields: {
      column1Title: optional(text("ستون ۱: عنوان", "خودروها", 24)),
      column1Links: optional(
        menu("ستون ۱: پیوندها", [
          "همه خودروها | /vehicles",
          "خودروهای برقی | /vehicles?fuel=electric",
          "خودروهای هیبریدی | /vehicles?fuel=hybrid",
          "مقایسه خودروها | /compare",
        ]),
      ),
      column2Title: optional(text("ستون ۲: عنوان", "پنل مشتریان", 24)),
      column2Links: optional(
        menu("ستون ۲: پیوندها", [
          "رزرو سرویس | /panel/appointments/new",
          "پیگیری تعمیر | /panel/appointments",
          "سفارش‌های در راه | /panel/orders",
          "امداد جاده‌ای و قطعات | /panel/requests",
          "سوابق و فاکتورها | /panel/history",
        ]),
      ),
      column3Title: optional(text("ستون ۳: عنوان", "راهنمای خرید", 24)),
      // the guide page came later, and the home page lost its installment-calculator and valuation
      // sections (/#tools, /#trade went nowhere): a stored copy of an earlier default is read as the
      // current one. The consult form takes the valuation request («معاوضه و ارزش خودروی فعلی»).
      column3Links: replacing(
        optional(
          menu("ستون ۳: پیوندها", [
            "مراحل خرید و انواع پلاک | /guide",
            "درخواست مشاوره | /#consult",
            "ارزش‌گذاری خودرو | /#consult",
            "راهنماها | /#guides",
          ]),
        ),
        [
          "درخواست مشاوره | /#consult",
          "محاسبه اقساط | /#tools",
          "ارزش‌گذاری خودرو | /#trade",
          "راهنماها | /#guides",
        ].join("\n"),
        [
          "مراحل خرید و انواع پلاک | /guide",
          "درخواست مشاوره | /#consult",
          "محاسبه اقساط | /#tools",
          "ارزش‌گذاری خودرو | /#trade",
          "راهنماها | /#guides",
        ].join("\n"),
      ),
      column4Title: optional(text("ستون ۴: عنوان", "تابان خودرو", 24)),
      column4Links: optional(
        menu("ستون ۴: پیوندها", [
          "درباره ما | /about",
          "شعب | /#branches",
          "تماس با ما | /contact",
        ]),
      ),
      bottomLinks: optional(
        menu(
          "ردیف پایین",
          ["حریم خصوصی | /privacy", "شرایط استفاده | /terms", "دسترس‌پذیری | /accessibility"],
          6,
        ),
      ),
    },
    check: groupsComplete([
      ["column1Title", "column1Links", "ستون ۱"],
      ["column2Title", "column2Links", "ستون ۲"],
      ["column3Title", "column3Links", "ستون ۳"],
      ["column4Title", "column4Links", "ستون ۴"],
    ]),
  },
  mobileBar: {
    title: "نوار پایین موبایل",
    description:
      "نوار ثابت پایین صفحه در موبایل: تا پنج دکمه، هر کدام با عنوان کوتاه، مسیر و آیکن. مسیر با / (صفحه‌های سایت، مثلاً /vehicles یا /#consult)، https:// (سایت دیگر، در برگه تازه) یا tel: شروع می‌شود.",
    fields: {
      item1,
      item1Icon,
      item2,
      item2Icon,
      item3,
      item3Icon,
      item4,
      item4Icon,
      item5,
      item5Icon,
    },
    check: (values) =>
      ([1, 2, 3, 4, 5] as const).some((n) => values[`item${n}`])
        ? null
        : "دست‌کم یک دکمه برای نوار پایین موبایل بنویسید.",
  },
  contact: {
    title: "تماس، سربرگ و پاورقی",
    description:
      "تلفن فروش و شماره امداد (سربرگ، منوی موبایل، پاورقی، صفحه «تماس با ما» و پنل مشتریان)، ساعت‌های نوار بالا، متن‌های پاورقی و نمادهای اعتماد پاورقی (مثل اینماد و ساماندهی؛ پیوند و نشانی تصویر را از کد نمادی که صادرکننده داده بردارید).",
    fields: {
      salesPhone: optional({
        label:
          "تلفن فروش (دکمه «تماس با فروش» سربرگ و منوی موبایل، پاورقی و صفحه تماس؛ خالی: دکمه به صفحه «تماس با ما» و شماره شعب می‌رود)",
        kind: "phone",
        max: 20,
        initial: "",
      }),
      hotlineLabel: text("عنوان شماره امداد", "امداد فوری", 30),
      hotlinePhone: optional({
        label: "شماره امداد ۲۴ ساعته (خالی: شماره امداد در سایت و پنل نمایش داده نمی‌شود)",
        kind: "phone",
        max: 20,
        initial: "",
      }),
      salesHours: text("ساعت فروش (نوار بالای سایت)", "فروش تا ۲۰:۰۰", 30),
      serviceHours: text("ساعت خدمات (نوار بالای سایت)", "خدمات تا ۱۸:۰۰", 30),
      menuSubtitle: text("زیرعنوان منوی موبایل", "خرید خودرو و خدمات مالکیت"),
      tagline: paragraph(
        "شعار پاورقی",
        "یک مسیر روشن برای خرید خودرو، نگهداری و پشتیبانی مالکیت.",
        160,
      ),
      // the earlier default called the live site's prices a demo
      footerNote: replacing(
        optional(
          text(
            "یادداشت پایین پاورقی، مثلاً نام ثبتی، شماره ثبت و نشانی شرکت (خالی: نمایش داده نمی‌شود)",
            "",
            240,
          ),
        ),
        "اطلاعات و قیمت‌های این نسخه نمایشی هستند.",
      ),
      seal1Link: replacing(sealLink(1, ENAMAD_LINK), ""),
      seal1Image: replacing(sealImage(1, ENAMAD_IMAGE), ""),
      seal2Link: sealLink(2),
      seal2Image: sealImage(2),
    },
    check: (values) => {
      for (const n of [1, 2] as const) {
        const link = values[`seal${n}Link`];
        const image = values[`seal${n}Image`];
        if (Boolean(link) !== Boolean(image)) {
          return `نماد ${faPlain.format(n)}: پیوند و تصویر را با هم پر کنید یا هر دو را خالی بگذارید.`;
        }
        if (image && !SEAL_IMAGE.test(image)) {
          return `نماد ${faPlain.format(n)}: نشانی تصویر باید با https:// شروع شود و فاصله نداشته باشد.`;
        }
        if (link && !menuLinks(link)[0]?.external) {
          return `نماد ${faPlain.format(n)}: پیوند باید به سایت صادرکننده‌ی نماد (https://) برود.`;
        }
      }
      return null;
    },
  },
  panelDocuments: {
    title: "مدارک خودرو در پنل مشتری",
    description:
      "نوع‌های مدرکی که مشتری و کارشناس برای خودرو ثبت می‌کنند. کارت خودرو، بیمه شخص ثالث و معاینه فنی همیشه هستند؛ نوع‌های دیگر را اینجا بنویسید.",
    fields: {
      extraTypes: optional(
        list(
          "نوع‌های دیگر مدرک: هر خط یک نوع؛ برای مدرکی که تاریخ انقضا دارد و باید یادآوری شود، «| انقضا» را به آخر خط اضافه کنید (مثلاً «بیمه بدنه | انقضا»)",
          ["بیمه بدنه | انقضا", "کارت سوخت", "سند خودرو", "کارت گارانتی"],
          800,
        ),
      ),
    },
  },
  liveChat: {
    title: "گفتگوی آنلاین سایت",
    description:
      "دکمه گفتگو گوشه همه صفحه‌های سایت و پنل مشتریان؛ پاسخ‌ها از بخش «گفتگوی آنلاین» پنل مدیریت داده می‌شود.",
    fields: {
      enabled: {
        label: "نمایش دکمه گفتگو در سایت",
        kind: "choice",
        max: 4,
        initial: "on",
        options: [
          { value: "on", label: "نمایش داده شود" },
          { value: "off", label: "پنهان باشد" },
        ],
      },
      title: text("عنوان پنجره گفتگو", "گفتگو با پشتیبانی تابان", 40),
      greeting: paragraph(
        "پیام خوشامد (اولین پیام پنجره)",
        "سلام! درباره خرید، اقساط، سفارش یا خدمات خودرو بپرسید؛ کارشناسان ما همین‌جا پاسخ می‌دهند.",
        200,
      ),
      offlineNote: paragraph(
        "متن وقتی کارشناسی آنلاین نیست",
        "الان کارشناسی آنلاین نیست؛ پیامتان را بگذارید و شماره موبایل بدهید تا پاسخ را پیامک کنیم.",
        200,
      ),
    },
  },
  shipping: {
    title: "سایت تابان شیپ",
    description: "تماس، نشانی و سؤالات متداول سایت shipping.tabankhodro.com",
    fields: {
      phone: optional({
        label: "تلفن تماس (خالی: شماره نمایش داده نمی‌شود)",
        kind: "phone",
        max: 20,
        initial: "",
      }),
      whatsapp: optional({
        label: "شماره واتس‌اپ؛ خالی: دکمه واتس‌اپ نمایش داده نمی‌شود",
        kind: "phone",
        max: 20,
        initial: "",
      }),
      address: text("نشانی شرکت", TABAN_SHIP_COMPANY.address, 160),
      hours: optional(text("ساعت کاری (خالی: نمایش داده نمی‌شود)", "", 60)),
      faq: list("سؤالات متداول: هر خط «سؤال | پاسخ»", SHIPPING_FAQ, 4000),
    },
    check: (values) =>
      lines(values.faq ?? "").every((line) => parseFaq(line))
        ? null
        : "هر خط سؤالات متداول باید «سؤال | پاسخ» باشد.",
  },
  seo: {
    title: "عنوان و توضیح سایت در گوگل",
    description: "عنوان پیش‌فرض برگه مرورگر و توضیحی که موتورهای جستجو زیر نام سایت نشان می‌دهند.",
    fields: {
      title: text("عنوان سایت", "تابان خودرو | خرید خودرو و خدمات مالکیت", 70),
      description: paragraph(
        "توضیح سایت",
        "جستجوی خودرو، محاسبه اقساط، رزرو سرویس و پیگیری خدمات خودرو در یک مسیر روشن.",
        160,
      ),
    },
  },
} satisfies Record<string, SiteBlock>;

export type SiteBlockKey = keyof typeof siteBlocks;
/** Every block in the panel's order; the tests check none is missing */
export const siteBlockKeys = [
  "hero",
  "homeShowcase",
  "homeTools",
  "finance",
  "payment",
  "homeStories",
  "vehiclesPage",
  "vehiclePage",
  "comparePage",
  "aboutPage",
  "termsPage",
  "privacyPage",
  "accessibilityPage",
  "guidePage",
  "headerMenu",
  "footerMenu",
  "mobileBar",
  "contact",
  "panelDocuments",
  "liveChat",
  "shipping",
  "seo",
] as const satisfies readonly SiteBlockKey[];

/** Every block's values as the site renders them */
export type SiteContent = {
  [Key in SiteBlockKey]: { [Field in keyof (typeof siteBlocks)[Key]["fields"]]: string };
};

/** The items of a `lines` field */
export const lines = (value: string) =>
  value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

/** Persian and Arabic digits as Latin ones (both blocks start at a code point ending in 0: U+06F0, U+0660) */
const latinDigits = (value: string) =>
  value.replace(/[۰-۹٠-٩]/g, (digit) => String(digit.charCodeAt(0) % 16));

/** A `phone` field as a tel: link (Persian digits and separators dropped) */
export const telHref = (phone: string) => `tel:${latinDigits(phone).replace(/[^\d+]/g, "")}`;

const PHONE = /^\+?[\d۰-۹][\d۰-۹\s-]{2,19}$/;

export type MenuLink = { label: string; href: string; external: boolean };

const LINK_LABEL_MAX = 40;
/**
 * Where a menu link may point: a path of this site (not «//», which is another host), an
 * anchor, another site over https, a phone or an email. Nothing else (javascript:, http:).
 */
const LINK_HREF =
  /^(?:\/(?![/\\])\S*|#\S+|https:\/\/[^\s/?#]+\S*|tel:\+?\d{3,15}|mailto:[^\s@]+@[^\s@]+\.[^\s@]+)$/;

/** One «عنوان | /مسیر» menu line, or null when it is not one */
function parseLink(line: string): MenuLink | null {
  const parts = line.split("|");
  if (parts.length !== 2) return null;
  const [rawLabel = "", rawHref = ""] = parts;
  const label = rawLabel.trim();
  const href = rawHref.trim().startsWith("tel:")
    ? latinDigits(rawHref).replace(/[\s-]/g, "")
    : rawHref.trim();
  if (!label || label.length > LINK_LABEL_MAX || !LINK_HREF.test(href)) return null;
  return { label, href, external: href.startsWith("https://") };
}

/** The links of a `links` field (a stored line that is not a link is left out) */
export const menuLinks = (value: string) =>
  lines(value).flatMap((line) => {
    const link = parseLink(line);
    return link ? [link] : [];
  });

/** One «سؤال | پاسخ» line, or null when it is not one (a single « | », text on both sides) */
function parseFaq(line: string) {
  const parts = line.split(" | ");
  const [question = "", answer = ""] = parts.map((part) => part.trim());
  return parts.length === 2 && question && answer ? { question, answer } : null;
}

/** The questions of the shipping site's FAQ (a stored line that is not one is left out) */
export const faqItems = (value: string) =>
  lines(value).flatMap((line) => {
    const item = parseFaq(line);
    return item ? [item] : [];
  });

export type ServiceCard = { name: string; tagline: string; text: string[]; items: string[] };

/**
 * The «خدمات» field of «درباره ما»: «## نام» opens a card, its next line is the tagline, later lines
 * are text and «- » lines its items; lines before the first «## » are left out
 */
export function serviceCards(value: string): ServiceCard[] {
  const cards: ServiceCard[] = [];
  for (const line of lines(value)) {
    const name = /^##\s+(.+)$/.exec(line);
    if (name?.[1]) {
      cards.push({ name: name[1], tagline: "", text: [], items: [] });
      continue;
    }
    const card = cards.at(-1);
    const item = /^[-–•]\s*(.+)$/.exec(line);
    if (!card) continue;
    if (item?.[1]) card.items.push(item[1]);
    else if (card.tagline) card.text.push(line);
    else card.tagline = line;
  }
  return cards;
}

export type PageSection = { kind: "heading" | "paragraph"; text: string };

/** A static page's body: blank lines split paragraphs, a line starting with «## » is a subheading */
export function pageSections(body: string): PageSection[] {
  const sections: PageSection[] = [];
  let paragraph: string[] = [];
  const flush = () => {
    if (paragraph.length > 0) sections.push({ kind: "paragraph", text: paragraph.join(" ") });
    paragraph = [];
  };
  for (const raw of body.split(/\r?\n/)) {
    const line = raw.trim();
    // «## » and its text; a bare «##» (its space trimmed away) is an empty heading, left out
    const heading = /^##(?:\s+(.*))?$/.exec(line);
    if (heading) {
      flush();
      if (heading[1]) sections.push({ kind: "heading", text: heading[1] });
    } else if (line) {
      paragraph.push(line);
    } else {
      flush();
    }
  }
  flush();
  return sections;
}

/** The rule a field's value must pass to be saved (and to be shown, once stored) */
export function fieldSchema(field: SiteField) {
  const empty = z.literal("");
  switch (field.kind) {
    case "image":
      return field.optional ? z.url().or(empty) : z.url("یک تصویر بارگذاری کنید");
    case "choice": {
      const values = (field.options ?? []).map((option) => option.value);
      return z.string().refine((value) => values.includes(value), "یکی از گزینه‌ها را انتخاب کنید");
    }
    case "phone": {
      const phone = z.string().regex(PHONE, "فقط رقم، فاصله و خط تیره");
      return z
        .string()
        .trim()
        .transform((value) =>
          latinDigits(value).replace(/\D/g, "") === PLACEHOLDER_PHONE ? "" : value,
        )
        .pipe(field.optional ? phone.or(empty) : phone);
    }
    case "number":
    case "numbers": {
      const [min, max] = field.range ?? [0, 100];
      const outside = `فقط عدد صحیح بین ${faPlain.format(min)} تا ${faPlain.format(max)}`;
      const wholes = z
        .string()
        // Persian digits as typed in the panel; stored with Latin digits, as the site reads them
        .transform((value) => latinDigits(value).trim())
        .refine(
          (value) =>
            value.length <= field.max &&
            (field.kind === "numbers" ? /^\d+([\s,،]+\d+)*$/ : /^\d+$/).test(value) &&
            numbers(value).every((item) => item >= min && item <= max),
          outside,
        )
        .transform((value) => [...new Set(numbers(value))].sort((a, b) => a - b).join(", "));
      return field.optional ? wholes.or(empty) : wholes;
    }
    case "lines":
      return z
        .string()
        .transform((value) => lines(value).join("\n"))
        .pipe(
          z
            .string()
            .min(field.optional ? 0 : 1, "دست‌کم یک خط بنویسید")
            .max(field.max, `حداکثر ${field.max} نویسه`),
        );
    case "links": {
      const most = field.items ?? 12;
      return z
        .string()
        .transform((value, ctx) => {
          const items = lines(value);
          if (items.length > most) {
            ctx.addIssue({ code: "custom", message: `حداکثر ${faPlain.format(most)} پیوند` });
            return z.NEVER;
          }
          const tidy: string[] = [];
          for (const item of items) {
            const link = parseLink(item);
            if (!link) {
              ctx.addIssue({
                code: "custom",
                message: `«${item.slice(0, LINK_LABEL_MAX)}» را به شکل «عنوان | /مسیر» بنویسید (عنوان تا ${faPlain.format(LINK_LABEL_MAX)} نویسه؛ مسیر با / یا # یا https:// یا tel: شروع شود)`,
              });
              return z.NEVER;
            }
            tidy.push(`${link.label} | ${link.href}`);
          }
          return tidy.join("\n");
        })
        .pipe(
          z
            .string()
            .min(field.optional ? 0 : 1, "دست‌کم یک پیوند بنویسید")
            .max(field.max, `حداکثر ${field.max} نویسه`),
        );
    }
    default:
      return z
        .string()
        .trim()
        .min(field.optional ? 0 : 1, "این متن خالی است")
        .max(field.max, `حداکثر ${field.max} نویسه`);
  }
}

/** A saved block as stored (jsonb): field name to whatever was written */
const storedFields = z.record(z.string(), z.unknown()).catch({});
type StoredFields = z.infer<typeof storedFields>;

/** A stored block over its defaults, field by field: a value that no longer passes its rule falls back alone */
export function readBlock(block: SiteBlock, stored: StoredFields): Record<string, string> {
  return Object.fromEntries(
    Object.entries(block.fields).map(([key, field]) => {
      if (field.retired?.some((old) => old === stored[key])) return [key, field.initial];
      const parsed = fieldSchema(field).safeParse(stored[key]);
      return [key, parsed.success ? parsed.data : field.initial];
    }),
  );
}

/** The whole site's content from the saved rows (a block without a row shows its defaults) */
export function readSiteContent(rows: readonly { key: string; value: unknown }[]): SiteContent {
  const content = Object.fromEntries(
    siteBlockKeys.map((key) => [
      key,
      readBlock(siteBlocks[key], storedFields.parse(rows.find((row) => row.key === key)?.value)),
    ]),
  );
  // SAFETY: one entry per SiteBlockKey, each holding exactly that block's own fields as strings (readBlock)
  return content as SiteContent;
}

type BlockCheck = { ok: true; values: Record<string, string> } | { ok: false; reason: string };

/** Staff's form for one block, checked field by field; the first problem as the panel's Persian refusal */
export function checkBlock(key: SiteBlockKey, values: Record<string, string>): BlockCheck {
  const block: SiteBlock = siteBlocks[key];
  const checked: Record<string, string> = {};
  for (const [name, field] of Object.entries(block.fields)) {
    const parsed = fieldSchema(field).safeParse(values[name] ?? "");
    if (!parsed.success) {
      return {
        ok: false,
        reason: `${field.label}: ${parsed.error.issues[0]?.message ?? "مقدار نادرست"}`,
      };
    }
    checked[name] = parsed.data;
  }
  const problem = block.check?.(checked);
  if (problem) return { ok: false, reason: problem };
  return { ok: true, values: checked };
}

/** The header's menus: dropdown groups and plain links (desktop) and the mobile menu */
export function headerMenu(menu: SiteContent["headerMenu"]) {
  const groups = [
    { title: menu.group1Title, note: menu.group1Note, links: menuLinks(menu.group1Links) },
    { title: menu.group2Title, note: menu.group2Note, links: menuLinks(menu.group2Links) },
    { title: menu.group3Title, note: menu.group3Note, links: menuLinks(menu.group3Links) },
  ];
  return {
    groups: groups.filter((group) => group.title && group.links.length > 0),
    links: menuLinks(menu.links),
    mobile: menuLinks(menu.mobileLinks),
  };
}

/** The mobile bottom bar's buttons in order; an emptied button is left out, an unknown icon falls back to the car */
export function mobileBar(bar: SiteContent["mobileBar"]) {
  return ([1, 2, 3, 4, 5] as const).flatMap((n) => {
    const link = menuLinks(bar[`item${n}`])[0];
    const icon = bar[`item${n}Icon`];
    if (!link) return [];
    return [
      { ...link, icon: mobileBarIcons.find((option) => option.value === icon)?.value ?? "car" },
    ];
  });
}

/** The footer's link columns and its bottom row */
export function footerMenu(menu: SiteContent["footerMenu"]) {
  const columns = ([1, 2, 3, 4] as const).map((n) => ({
    title: menu[`column${n}Title`],
    links: menuLinks(menu[`column${n}Links`]),
  }));
  return {
    columns: columns.filter((column) => column.title && column.links.length > 0),
    bottom: menuLinks(menu.bottomLinks),
  };
}

/** The footer's trust seals staff set (link and image both present), in order */
export const footerSeals = (contact: SiteContent["contact"]) =>
  ([1, 2] as const).flatMap((n) => {
    const link = menuLinks(contact[`seal${n}Link`])[0];
    const image = contact[`seal${n}Image`];
    return link && SEAL_IMAGE.test(image) ? [{ ...link, image }] : [];
  });

/** One static page's content (about, terms, privacy, accessibility, guide share the same fields) */
export type SitePage = SiteContent["termsPage"];

type Finance = SiteContent["finance"];

/**
 * The installment calculators' limits. Every field passed its own rule, but a block saved
 * before a cross-field rule existed (or one field falling back alone) may not agree, so the
 * default is pulled inside the range and onto an offered month count.
 */
export function financeCalculator(finance: Finance) {
  const annualRate = Number(finance.annualRate);
  const months = numbers(finance.monthOptions);
  const downMin = Math.min(Number(finance.downMin), Number(finance.downMax));
  const downMax = Math.max(Number(finance.downMin), Number(finance.downMax));
  const monthDefault = Number(finance.monthDefault);
  return {
    annualRate,
    months,
    monthDefault: months.includes(monthDefault) ? monthDefault : (months[0] ?? 12),
    downMin,
    downMax,
    downDefault: Math.min(downMax, Math.max(downMin, Number(finance.downDefault))),
  };
}

/** The account customers transfer to (digits Latin, spaces dropped); null when none is set */
export function paymentAccount(payment: SiteContent["payment"]) {
  const card = latinDigits(payment.cardNumber).replace(/[\s-]/g, "");
  const sheba = latinDigits(payment.sheba).replace(/[\s-]/g, "").toUpperCase();
  if (!card && !sheba) return null;
  return {
    bankName: payment.bankName,
    holder: payment.holder,
    cardNumber: card,
    sheba,
    accountNumber: latinDigits(payment.accountNumber).trim(),
    instructions: payment.instructions,
  };
}

/** The installment late-fee rule of the payment block (see lateFeeFor in payments/config) */
export const lateFeeRule = (payment: SiteContent["payment"]) => ({
  percentPerMonth: Number(payment.lateFeePercent) || 0,
  graceDays: Number(payment.lateFeeGraceDays) || 0,
});
